import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaRegHeart, FaShoppingCart, FaPlus, FaMinus } from "react-icons/fa";
import RelatedProduct from "./RelatedProduct";
import StarRating from "../../components/Ratings/StarRating";
import StarDisplay from "../../components/Ratings/StarDisplay";
import toast from "react-hot-toast";
import { useCart } from "../../hooks/CartContext";
import { useWish } from "../../hooks/WishContext";
import useAuthUser from "../../components/common/useAuthUser";
import RatingDetails from "./RatingDetails";

const ProductDetails = () => {
  const { id } = useParams();
  const token = Cookies.get("gameport");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { addToCart } = useCart();
  const { addToWish } = useWish();
  const { authUser } = useAuthUser();

  const [rating, setRating] = useState(0);
  const [activeImg, setActiveImg] = useState(null);
  const [qty, setQty] = useState(1);

  const API_URL = process.env.REACT_APP_API_URL;

  const {
    data: productRes,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["product", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/products/${id}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch product");
      }
      console.log(data)
      return data;
    },
  });

  const product = productRes?.product || productRes;
  const images = product?.images ?? [];
  const stock = Number(product?.quantity || 0);
  const soldOut = stock < 1;

  useEffect(() => {
    if (images.length > 0) {
      setActiveImg(images[0]);
    }
  }, [images]);

  useEffect(() => {
    if (soldOut) {
      setQty(1);
    } else {
      setQty((prev) => Math.min(Math.max(prev, 1), stock));
    }
  }, [soldOut, stock]);

  const price = useMemo(() => Number(product?.price || 0), [product?.price]);
  const finalPrice = useMemo(
    () => Number(product?.finalPrice || 0),
    [product?.finalPrice]
  );
  const saveAmount = useMemo(
    () => Math.max(0, price - finalPrice),
    [price, finalPrice]
  );

  const infoCards = useMemo(() => {
    return [
      { label: "Category", value: product?.category },
      { label: "Brand", value: product?.brand },
      { label: "Condition", value: product?.condition },
      { label: "Platform", value: product?.platform },
    ].filter((item) => item.value);
  }, [product]);

  const {
    data: productRatings,
    isPending: ratingLoading,
  } = useQuery({
    queryKey: ["product-ratings", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await fetch(`${API_URL}/products/rate/${id}/rates`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch ratings");
      }

      return data;
    },
  });

  const { mutate: rateProduct, isPending: isRatingLoading } = useMutation({
    mutationKey: ["rate-product", id],
    mutationFn: async (value) => {
      if (!token) throw new Error("Please login to rate products");
      if (!value) throw new Error("Select a rating first");

      const res = await fetch(`${API_URL}/products/${id}/rate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: value }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to submit rating");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      queryClient.invalidateQueries({ queryKey: ["product-ratings", id] });
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      toast.success("Rating submitted");
      setRating(0);
    },
    onError: (err) => toast.error(err.message),
  });

  const { mutate: featureProduct, isPending: isFeaturing } = useMutation({
    mutationKey: ["feature-product", id],
    mutationFn: async () => {
      if (!token) throw new Error("Not authorized");

      const res = await fetch(`${API_URL}/admin/feature/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to feature product");
      }

      return data;
    },
    onSuccess: () => {
      toast.success("Product featured");
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (err) => toast.error(err.message),
  });

  const handleDecreaseQty = () => {
    setQty((prev) => Math.max(1, prev - 1));
  };

  const handleIncreaseQty = () => {
    if (soldOut) return;

    if (qty >= stock) {
      toast.error("You have reached the available stock.");
      return;
    }

    setQty((prev) => prev + 1);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();

    if (soldOut) return;

    addToCart(product, qty);
  };

  const handleAddToWish = (e) => {
    e.stopPropagation();
    addToWish(product);
  };

  if (isPending) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <p className="text-gray-600">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <p className="text-sm text-red">{error?.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <p className="text-gray-600">Product not found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-8 sm:px-6 sm:py-10">
        <div className="grid grid-cols-1 gap-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:gap-8 sm:p-7 md:grid-cols-2">
          {/* Left Side */}
          <div className="space-y-4">
            <div className="flex items-center justify-center rounded-2xl border border-gray-100 bg-gray-50 p-4 sm:p-6">
              <img
                src={activeImg || images?.[0] || "/placeholder.png"}
                alt={product?.name || "product"}
                className="h-64 w-full object-contain sm:h-80"
              />
            </div>

            {images.length > 1 && (
              <div className="flex flex-wrap gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImg(img)}
                    className={`rounded-xl border bg-white p-2 transition ${
                      activeImg === img
                        ? "border-gray-900"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`thumb-${i}`}
                      className="h-14 w-14 rounded-lg object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Side */}
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              {product?.isFeatured && (
                <span className="rounded-full bg-red px-3 py-1.5 text-xs font-semibold text-white">
                  Featured
                </span>
              )}

              <span
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  soldOut
                    ? "border-gray-200 bg-white text-red"
                    : "border-gray-200 bg-white text-gray-700"
                }`}
              >
                {soldOut ? "Out of stock" : "In stock"}
              </span>

              {!soldOut && (
                <span className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">
                  {stock} unit{stock !== 1 ? "s" : ""} available
                </span>
              )}

              {product?.condition && (
                <span className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">
                  {product.condition}
                </span>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
              {product?.name}
            </h1>

            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
              <StarDisplay rating={Math.round(product?.averageRating || 0)} />
              <span>
                {Number(product?.averageRating || 0).toFixed(1)} out of 5 (
                {product?.ratings?.length || 0} ratings)
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="text-2xl font-extrabold text-gray-900">
                ₦{finalPrice.toLocaleString()}
              </span>
{
  product?.discount > 0 && <> {!!price && price !== finalPrice && (
                <span className="text-gray-500 line-through">
                  ₦{price.toLocaleString()}
                </span>
              )}</>
}
             
{
      product?.discount >0? <>
        {saveAmount > 0 && (
                <span className="rounded-full bg-red px-3 py-1.5 text-xs font-semibold text-white">
                  Save ₦{saveAmount.toLocaleString()}
                </span>
              )}
      </>: <></>
}
            

              {authUser?.role === "admin" && (
                <button
                  onClick={() => navigate(`/admin/update-product/${id}`)}
                  className="ml-auto rounded-xl bg-red px-4 py-2 font-semibold text-white transition hover:opacity-90"
                >
                  Edit
                </button>
              )}
            </div>

            <div className="space-y-3 rounded-2xl border border-gray-200 p-4 sm:p-5">
              <h2 className="font-semibold text-gray-900">Product Description</h2>

              <p className="text-sm leading-relaxed text-gray-600">
                {product?.description || "—"}
              </p>

              {infoCards.length > 0 && (
                <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
                  {infoCards.map((item) => (
                    <div key={item.label} className="rounded-xl bg-gray-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {item.label}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <ul className="space-y-1 pt-2 text-sm text-gray-600">
                <li>✓ Delivery available</li>
                <li>✓ Warranty (if applicable)</li>
                <li>✓ Returns policy available</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="flex items-center justify-between rounded-2xl border border-gray-200 p-4">
                <div>
                  <p className="font-semibold text-gray-900">Quantity</p>
                  {!soldOut && (
                    <p className="mt-1 text-xs text-gray-500">Max: {stock}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDecreaseQty}
                    disabled={soldOut}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                  >
                    <FaMinus />
                  </button>

                  <span className="w-10 text-center font-semibold text-gray-900">
                    {qty}
                  </span>

                  <button
                    type="button"
                    onClick={handleIncreaseQty}
                    disabled={soldOut || qty >= stock}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 p-4">
                <p className="font-semibold text-gray-900">Rate this product</p>
                <div className="mt-2">
                  <StarRating rating={rating} setRating={setRating} />
                </div>
                <button
                  type="button"
                  disabled={isRatingLoading}
                  onClick={() => rateProduct(rating)}
                  className={`mt-3 w-full rounded-xl py-2.5 font-semibold transition ${
                    isRatingLoading
                      ? "cursor-not-allowed bg-gray-200 text-gray-600"
                      : "bg-red text-white hover:opacity-90"
                  }`}
                >
                  {isRatingLoading ? "Submitting..." : "Submit Rating"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <button
                onClick={handleAddToCart}
                disabled={soldOut}
                className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 font-semibold transition ${
                  soldOut
                    ? "cursor-not-allowed bg-gray-200 text-gray-600"
                    : "bg-red text-white hover:opacity-90"
                }`}
              >
                <FaShoppingCart />
                {soldOut ? "Sold Out" : "Add to Cart"}
              </button>

              <button
                onClick={handleAddToWish}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 py-3 font-semibold text-gray-900 transition hover:bg-gray-50"
              >
                <FaRegHeart />
                Wishlist
              </button>

              {authUser?.role === "admin" && (
                <button
                  onClick={() => featureProduct()}
                  disabled={isFeaturing}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 font-semibold transition ${
                    isFeaturing
                      ? "cursor-not-allowed bg-gray-200 text-gray-600"
                      : "bg-red text-white hover:opacity-90"
                  }`}
                >
                  <FaPlus />
                  {isFeaturing ? "Featuring..." : "Feature"}
                </button>
              )}
            </div>
          </div>
        </div>

        <RelatedProduct category={product?.category} />

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-7">
          <h2 className="mb-4 text-xl font-bold text-gray-900">Reviews</h2>
          <RatingDetails ratings={productRatings} loading={ratingLoading} />
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;